// ÍGF úrgangs-gerðir — speglað úr Lausnir minar-sidur/lib/igf-types.ts.
// Ekki nota Zod fyrir aðal-runtime-gerðirnar (þær eru of breiðar) — eingöngu
// fyrir API-parameter-validation á borð við CompanyIdSchema í companies.ts.

export type MaterialCategory =
  | 'malmar'
  | 'timbur'
  | 'steinefni'
  | 'plast'
  | 'pappir'
  | 'gler'
  | 'lifraen'
  | 'oflokkadur'
  | 'byggingarurgangur'
  | 'spilliefni'
  | 'raftaeki'
  | 'jardefni'
  | 'orkuvinnsla'
  | 'annad'

export type DisposalMethod = 'endurvinnsla' | 'orkunyting' | 'brennsla' | 'urdun'

export interface CostBreakdown {
  hraefni: number
  losun: number
  akstur: number
  leiga: number
  annad: number
  total: number
}

export const EMPTY_COST_BREAKDOWN: CostBreakdown = Object.freeze({
  hraefni: 0,
  losun: 0,
  akstur: 0,
  leiga: 0,
  annad: 0,
  total: 0,
})

export type CostType = 'hraefni' | 'losun' | 'akstur' | 'leiga' | 'annad'

export type CostSource = 'real' | 'estimated' | 'mixed'

export type IncinerationClass = 'mixed' | 'clinical' | 'hazardousSolid' | 'hazardousLiquid' | 'organic'

export interface MaterialInfo {
  id: string
  name: string
  category: MaterialCategory
  disposal: DisposalMethod
  costPerKg: number
  split?: Partial<Record<DisposalMethod, number>>
  incineration?: IncinerationClass
}

export interface IGFMonthlyData {
  year: number
  month: number
  key: string
  totalKg: number
  recyclableKg: number
  sortingRate: number
  totalCost: number
  categories: Record<MaterialCategory, number>
  categoriesCost?: Record<MaterialCategory, number>
  groups: GroupTally[]
  collectionCount: number
  costBreakdown?: CostBreakdown
  costPending?: boolean
}

export interface IGFContainerData {
  id: string
  kg: number
  cost: number
  count: number
  firstDate: string
  lastDate: string
}

export interface IGFLocationData {
  location: string
  totalKg: number
  recyclableKg: number
  sortingRate: number
  totalCost: number
  collectionCount: number
  firstDate: string
  lastDate: string
  categories: Record<MaterialCategory, number>
  groups: GroupTally[]
  materials: { name: string; kg: number; cost: number }[]
  containers?: IGFContainerData[]
  costBreakdown?: CostBreakdown
}

export interface IGFSummary {
  totalKg: number
  recyclableKg: number
  sortingRate: number
  totalCost: number
  collectionCount: number
  locationCount: number
  materialCount: number
  costSource: CostSource
  realCostShare: number
  costBreakdown: CostBreakdown
  hasPricedSplit: boolean
}

/** Bucket label for records without a location — mirrors the server aggregator
 *  (Aggregator.cs), which is the source of truth for `IGFLocationData.location`. */
export const UNKNOWN_LOCATION = '(óþekkt)'

/** Canonical location key for a raw record: trimmed, blank → UNKNOWN_LOCATION.
 *  Every client-side grouping/filtering of raw records by location must use this
 *  so it matches the server-aggregated location labels. */
export function normalizeLocation(location: string | null | undefined): string {
  const trimmed = (location ?? '').trim()
  return trimmed || UNKNOWN_LOCATION
}

/** Raw per-collection record (mirror of Lausnir `Collection`). Emitted by the
 *  aggregator so tabs like Losanir / Kostnaður / Samanburður can drill down. */
export interface Collection {
  collectionId?: string
  containerId?: string | null
  custId?: string
  location: string | null
  weight: number
  dateCollected: string
  materialId: string | null
  materialName?: string
  materialGroup?: string
  materialInvoicedAmount?: number | null
  costBreakdown?: CostBreakdown
  costOnly?: boolean
  disposal?: DisposalMethod
  postcode?: string
  costPending?: boolean
}

export interface CustomerYearData {
  monthly: IGFMonthlyData[]
  locations: IGFLocationData[]
  summary: IGFSummary
  generatedAt: string
  /** Raw collection rows for the year (weight > 0). Optional for static data. */
  rawRecords?: Collection[]
}

export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mars', 'Apríl', 'Maí', 'Júní',
  'Júlí', 'Ágú', 'Sept', 'Okt', 'Nóv', 'Des',
] as const

export const MONTH_NAMES_FULL = [
  'Janúar', 'Febrúar', 'Mars', 'Apríl', 'Maí', 'Júní',
  'Júlí', 'Ágúst', 'September', 'Október', 'Nóvember', 'Desember',
] as const

export const CATEGORY_INFO: Record<MaterialCategory, { name: string; color: string }> = {
  malmar:            { name: 'Málmar',              color: '#5A6E78' },
  timbur:            { name: 'Timbur',              color: '#BEA064' },
  steinefni:         { name: 'Steinefni',           color: '#A0978A' },
  plast:             { name: 'Plast',               color: '#961E82' },
  pappir:            { name: 'Pappír',              color: '#A68A6F' },
  gler:              { name: 'Gler',                color: '#3FB6A8' },
  lifraen:           { name: 'Lífrænn',             color: '#5C8E3C' },
  oflokkadur:        { name: 'Óflokkaður',          color: '#141414' },
  byggingarurgangur: { name: 'Byggingarúrgangur',   color: '#005B96' },
  spilliefni:        { name: 'Spilliefni',          color: '#E10F1E' },
  raftaeki:          { name: 'Raftæki',             color: '#F0910A' },
  jardefni:          { name: 'Jarðefni',            color: '#78716C' },
  orkuvinnsla:       { name: 'Orkuvinnsla',         color: '#000000' },
  annad:             { name: 'Annað',               color: '#94A3B8' },
}

export const DISPOSAL_INFO: Record<DisposalMethod, { name: string; color: string }> = {
  endurvinnsla: { name: 'Endurvinnsla',             color: '#00A04B' },
  orkunyting:   { name: 'Brennsla með orkunýtingu', color: '#F0910A' },
  brennsla:     { name: 'Brennsla án orkunýtingar', color: '#E10F1E' },
  urdun:        { name: 'Urðun',                    color: '#141414' },
}

export const DISPOSAL_EXPLAIN: Record<DisposalMethod, { what: string; why: string; source: string }> = {
  endurvinnsla: {
    what: 'Efnið er flokkað og sent áfram í endurvinnslu, oftast erlendis, þar sem það verður hráefni í nýjar vörur.',
    why: 'Endurvinnslan sjálf fer að mestu fram utan landsteinanna. Í opinberu loftslagsbókhaldi Íslands telst sú losun til móttökulandsins (sbr. lög um loftslagsmál nr. 70/2012) og íslenski stuðullinn er því 0. Til að endurvinnslan hverfi ekki úr myndinni notum við breskan stuðul sem staðgengil. Hann nær yfir söfnun, flutning og fyrstu flokkun — ekki endurvinnsluferlið sjálft, sem í kolefnisbókhaldi telst til þess sem notar endurunna efnið.',
    source: 'UK Government GHG Conversion Factors (DESNZ) · ákvörðun ÍGF',
  },
  orkunyting: {
    what: 'Óflokkað sorp er pressað í bagga og flutt utan, þar sem það er brennt til að framleiða rafmagn og hita í stað jarðefnaeldsneytis.',
    why: 'Brennslan fer fram erlendis, og í loftslagsbókhaldi — sem lög um loftslagsmál (nr. 70/2012) byggja á — telst losunin til þess lands þar sem hún verður til, ekki Íslands. Við styðjumst við breskan stuðul sem staðgengil þar sem íslensk tala er ekki til. Hann er lágur af því hann nær aðeins yfir söfnun og flutning að fyrsta móttökustað; losunin frá brennslunni sjálfri er bókfærð hjá þeim sem nýtir orkuna. Áhrifin hverfa ekki — þau eru einfaldlega bókfærð annars staðar.',
    source: 'UK Government GHG Conversion Factors (DESNZ) · lög um loftslagsmál nr. 70/2012',
  },
  brennsla: {
    what: 'Efnið er brennt á Íslandi, í Kölku í Reykjanesbæ, án þess að orkan sé nýtt. Þangað fara t.d. dýrahræ og spilliefni.',
    why: 'Við brunann fara gróðurhúsalofttegundir út í loftið; aðeins kolefni úr jarðefnum (olía, plast) telur, ekki kolefni úr plöntum og dýrum. Stuðullinn fer því eftir því hvað er brennt: blandað sorp (UOS), sóttmengað, föst spilliefni og olíur eða leysiefni (IPCC-forsendur, staðgenglar þar til Kalka gefur upp eigin tölu), og lífrænn úrgangur með lága tölu.',
    source: 'Umhverfis- og orkustofnun, 7. útgáfa 2025',
  },
  urdun: {
    what: 'Sorpið er grafið á urðunarstað á Íslandi.',
    why: 'Þegar matarleifar og annað lífrænt rotnar ofan í jörðinni myndast metan, sem er miklu skaðlegra fyrir loftslagið en venjulegur útblástur. Aðeins hluta af því er náð — hitt sleppur út. Þess vegna er urðunarstuðullinn hæstur. Hann nær til losunar á urðunarstaðnum sjálfum, ekki flutnings.',
    source: 'Umhverfis- og orkustofnun, 7. útgáfa 2025',
  },
}

export interface GroupTally {
  group: string
  kg: number
  cost: number
}

export const MATERIAL_GROUPS = [
  'Plast', 'Pappír', 'Plast og pappír – óskipt',
  'Óflokkað efni/blandað efni', 'Lífrænn úrgangur', 'Timbur',
  'Málmar', 'Raftæki', 'Raftæki og málmar – óskipt',
  'Óvirkur úrgangur', 'Spilliefni', 'Annað',
] as const

export const UNKNOWN_GROUP = 'Óþekktur yfirflokkur'

export type MaterialGroup = (typeof MATERIAL_GROUPS)[number]

export const GROUP_INFO: Record<string, { color: string }> = {
  'Plast':                       { color: '#961E82' },
  'Pappír':                      { color: '#A68A6F' },
  'Plast og pappír – óskipt':    { color: '#C084B8' },
  'Óflokkað efni/blandað efni':  { color: '#141414' },
  'Lífrænn úrgangur':            { color: '#5C8E3C' },
  'Timbur':                      { color: '#BEA064' },
  'Málmar':                      { color: '#5A6E78' },
  'Raftæki':                     { color: '#F0910A' },
  'Raftæki og málmar – óskipt':  { color: '#9AA9B2' },
  'Óvirkur úrgangur':            { color: '#A0978A' },
  'Spilliefni':                  { color: '#E10F1E' },
  'Annað':                       { color: '#94A3B8' },
  [UNKNOWN_GROUP]:               { color: '#6B7280' },
}

export function groupColor(group: string): string {
  return GROUP_INFO[group]?.color ?? '#94A3B8'
}

export const ALL_CATEGORIES: MaterialCategory[] = [
  'malmar', 'timbur', 'steinefni', 'plast', 'pappir', 'gler', 'lifraen',
  'oflokkadur', 'byggingarurgangur', 'spilliefni', 'raftaeki', 'jardefni',
  'orkuvinnsla', 'annad',
]

export function emptyCategoryRecord(): Record<MaterialCategory, number> {
  return {
    malmar: 0, timbur: 0, steinefni: 0, plast: 0, pappir: 0, gler: 0,
    lifraen: 0, oflokkadur: 0, byggingarurgangur: 0, spilliefni: 0,
    raftaeki: 0, jardefni: 0, orkuvinnsla: 0, annad: 0,
  }
}

export function recycledShare(info: MaterialInfo): number {
  const split = info.split
  if (split) {
    const total = Object.values(split).reduce((s, v) => s + (v || 0), 0)
    if (total > 0) return (split.endurvinnsla || 0) / total
  }
  return isRecycledDisposal(info.disposal) ? 1 : 0
}

export function isRecycledDisposal(disposal: DisposalMethod): boolean {
  return disposal === 'endurvinnsla'
}
